import { z } from "zod";
import { PreferredContactMethodSchema } from "@remi/enums/preferred-contact-method";
import { PreferredLanguageSchema } from "@remi/enums/preferred-language";

import { PersonTagOptionSchema } from "./personTagOption";

const PhoneSchema = z.object({
  id: z.string(),
  number: z.string(),
  phoneNotes: z.string().nullable(),
});

const EmailSchema = z.object({
  id: z.string(),
  emailAddress: z.string(),
});

export const FORBIDDEN_NAME_REGEX = /[|:]/g;

export const UpdateHomeownerSchema = z.object({
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  phoneExtension: z.string().optional(),
  userIdForKnockUpdate: z.string().optional(),
  preferredLanguage: PreferredLanguageSchema.optional(),
  preferredContactMethod: PreferredContactMethodSchema.optional(),
  timeZone: z.string().optional(),
  phones: z.array(z.string()).optional(),
});

export const CreateHomeownerSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string().optional(),
  phoneExtension: z.string().optional(),
  userIdForKnockUpdate: z.string().optional(),
  preferredLanguage: PreferredLanguageSchema.optional(),
  preferredContactMethod: PreferredContactMethodSchema.optional(),
  timeZone: z.string().optional(),
  phones: z.array(z.string()).optional(),
});

export type CreateHomeowner = z.infer<typeof CreateHomeownerSchema>;

export const CreatePersonSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().optional(),
  phone: z.string().optional(),
  phones: z.array(z.object({ number: z.string(), phoneNotes: z.string().optional() })).optional(),
  emails: z.array(z.string()).optional(),
  tags: z.array(PersonTagOptionSchema).optional(),
  preferredLanguage: PreferredLanguageSchema,
  preferredContactMethod: PreferredContactMethodSchema.optional(),
  organizationId: z.string(),
});

export type CreatePerson = z.infer<typeof CreatePersonSchema>;

// Note: `jobTitle` is deliberately omitted here. It is self-serve-only via
// auth.updateMe (ODD-4139); admin flows that consume person.updatePerson
// cannot set or clear it.
export const UpdatePersonSchema = z.object({
  id: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().optional(),
  phone: z.string().optional(),
  phoneExtension: z.string().optional(),
  userIdForKnockUpdate: z.string().optional(),
  preferredLanguage: PreferredLanguageSchema.optional(),
  preferredContactMethod: PreferredContactMethodSchema.optional(),
  phones: z.array(PhoneSchema).optional(),
  emails: z.array(EmailSchema).optional(),
  bidId: z.number().optional(),
  organizationId: z.string().optional(),
  tags: z.array(PersonTagOptionSchema).optional(),
  manuallyEdited: z.boolean().optional(),
  // Removal needs its own signal: `email: ""` already means "nothing to set" at six call sites,
  // including a scope-of-work path where the homeowner simply skipped the field.
  removePrimaryEmail: z.literal(true).optional(),
});

export type UpdatePersonInput = z.infer<typeof UpdatePersonSchema>;
